import { useHomePageRevamp } from '../use-is-home-page';
import { DiscoverProjectsSectionLegacy } from './DiscoverProjectsSection.legacy';
import { DetailTileMedia } from '~/ui-rocco/pages/detail-shared/DetailTileMedia';
import { detailTileActionsFor } from '~/ui-rocco/pages/detail-shared/detail-tile-actions';
import type { ProjectTileViewModel } from '@rocco/ui/tile-project/view-models/project';
import { useState } from 'react';
import { useAtomValue } from 'jotai';
import { uiContextAtomDisplayMode } from '@rocco/states/uiContextAtomDisplayMode';
import type { CarouselApi } from '@rocco/components/carousel';
import { useIslandAutoplay } from '~/ui-rocco/pages/detail-shared/magic-island';
import { useLoaderData } from 'react-router';
import type { loader as homeLoader } from '~/routes/_app._index';
import {
    Carousel,
    CarouselContent,
    CarouselItem,
} from '@rocco/components/carousel';
import { useCarousel } from '@rocco/components/carousel/carousel';
import { RoccoLink } from '@rocco/components/link';
import { DiscoverHeader } from '../components/DiscoverHeader';
import { CarouselPaginationPill } from '../components/CarouselPaginationPill';
import { CarouselBrowseMoreTile } from '../components/CarouselBrowseMoreTile';
import { ScrollReveal } from '../components/ScrollReveal';
import { SplitHeading } from '../components/SplitHeading';
import type { ComponentProps, ReactNode } from 'react';
import { HOME_MOBILE_BLEED } from '../mobile-carousel-bleed';

/**
 * Discover Projects (Figma `997:38861`, black section).
 *
 * Shared editorial header above a horizontally-scrolling carousel of live
 * featured projects (`projectSection.tiles`) with a pagination pill.
 */
const ProjectsPagination = () => {
    const {
        api,
        currentSlide,
        totalSlides,
        scrollPrev,
        scrollNext,
        canScrollPrev,
        canScrollNext,
    } = useCarousel();

    // Hide pagination when every slide already fits (nothing to scroll).
    if (!canScrollPrev && !canScrollNext) return null;

    return (
        <div className="flex justify-center max-md:pt-8 md:pt-[2.5vw] lg:pt-[3rem]">
            <CarouselPaginationPill
                total={totalSlides}
                activeIndex={currentSlide}
                onPrev={scrollPrev}
                onNext={scrollNext}
                onSelect={(index) => api?.scrollTo(index)}
                label="Projects carousel"
                theme="dark"
            />
        </div>
    );
};

interface DiscoverProjectsSectionProps {
    /** Override the eyebrow (e.g. logged-in "Recently viewed"). */
    eyebrow?: string;
    /** Override the display heading (e.g. logged-in "Recently viewed Projects"). */
    heading?: ReactNode;
}

/** The hover action panel's config for a project tile, as the detail pages build it. */
const tileActions = (model: ProjectTileViewModel) =>
    detailTileActionsFor({
        id: model.id,
        link: model.link,
        title: model.title,
        website: model.website,
        pinType: 'ProjectPin',
        professional: {
            id: model.professional.id,
            name: model.professional.name,
        },
        professionalLink: model.professional.link,
    });

const DiscoverProjectsSectionRevamp = ({
    eyebrow = '(01 – Projects)',
    heading = <SplitHeading first="Discover" second="Projects" />,
}: DiscoverProjectsSectionProps = {}) => {
    const { projectSection, projectPopularCategories } =
        useLoaderData<typeof homeLoader>();
    // Drop tiles without a banner image — an empty grey card breaks the row.
    const tiles = (projectSection?.tiles ?? []).filter(
        (tile) => tile.model.images?.[0]?.src,
    );

    // On desktop the carousel's pagination and autoplay live in the Magic
    // Island, as the feature strip's do; its clock replaces the carousel's own.
    const { isMobile } = useAtomValue(uiContextAtomDisplayMode);
    const islandPagination = !isMobile;
    const [emblaApi, setEmblaApi] = useState<CarouselApi | null>(null);
    const islandRef = useIslandAutoplay<HTMLElement>(emblaApi, {
        label: 'Projects carousel',
        enabled: islandPagination && tiles.length > 1,
    });

    return (
        // The projects directory's near-black and light grey on desktop.
        <section className="w-full overflow-hidden bg-black text-white focus-ring-on-dark md:bg-[#141414] md:text-offwhite">
            <DiscoverHeader
                theme="dark"
                eyebrow={eyebrow}
                heading={heading}
                // Equal space above and below the heading on desktop: 112px on
                // the 1920 frame, half the average of the two old gaps.
                className="md:gap-[5.8333vw] lg:gap-[7rem]"
                headingGapClassName="md:gap-[5.8333vw] lg:gap-[7rem]"
                categories={projectPopularCategories}
                description="Explore completed homes, find the designs that inspire you, and connect directly with the architects who can turn your vision into reality."
                ctaLabel="View all Projects"
                ctaTo="/projects"
            />

            <ScrollReveal
                delay={120}
                className="max-md:px-6 max-md:pt-8 max-md:pb-8 md:px-[1.927vw] md:pt-[8.33vw] md:pb-[8.33vw] lg:px-[max(2.3124rem,calc((100%_-_120rem)/2_+_2.3124rem))] lg:pt-[9.996rem] lg:pb-[9.996rem]"
            >
                {/* The island follows the carousel, not the header above it. */}
                <div ref={islandPagination ? islandRef : undefined}>
                    <Carousel
                        setApi={setEmblaApi}
                        autoPlay={
                            islandPagination ? undefined : { delay: 4000 }
                        }
                        options={{
                            align: 'start',
                            dragFree: true,
                            duration: 35,
                            containScroll: 'trimSnaps',
                            // Wrap around at either end. Embla falls back to
                            // non-loop automatically when slides don't fill the view.
                            loop: true,
                        }}
                    >
                        {/* Loop-safe spacing: flex gap doesn't survive the loop
                            wrap-around, so the gap lives on each slide instead. */}
                        <CarouselContent
                            className="max-md:-mx-2"
                            viewportClassName={HOME_MOBILE_BLEED}
                        >
                            {tiles.map((tile) => (
                                <CarouselItem
                                    key={tile.model.id}
                                    className="max-md:basis-[calc(22.77rem+1rem)] max-md:px-2 md:basis-[84.13vw] md:px-[1.615vw] lg:basis-[100.956rem] lg:px-[1.938rem]"
                                >
                                    <RoccoLink
                                        to={tile.model.link}
                                        className="block w-full"
                                    >
                                        {/* The site's tile image: the same hover
                                            Save, Visit site, Enquire and Share
                                            as the project tiles elsewhere. */}
                                        <DetailTileMedia
                                            src={tile.model.images?.[0]?.src}
                                            alt={
                                                tile.model.images?.[0]?.alt ??
                                                tile.model.title
                                            }
                                            className="bg-neutral-800"
                                            style={{
                                                aspectRatio: '1553 / 868',
                                            }}
                                            imgClassName="max-w-none"
                                            pinId={tile.model.id}
                                            pinType="ProjectPin"
                                            {...tileActions(tile.model)}
                                        />
                                    </RoccoLink>
                                </CarouselItem>
                            ))}
                            {tiles.length > 0 && (
                                <CarouselBrowseMoreTile
                                    to="/projects"
                                    noun="Projects"
                                    theme="dark"
                                    itemClassName="max-md:basis-[calc(22.77rem+1rem)] max-md:px-2 md:basis-[84.13vw] lg:basis-[100.956rem] md:px-[1.615vw] lg:px-[1.938rem]"
                                    boxClassName="bg-neutral-800"
                                    boxStyle={{ aspectRatio: '1553 / 868' }}
                                />
                            )}
                        </CarouselContent>
                        {islandPagination ? null : <ProjectsPagination />}
                    </Carousel>
                </div>
            </ScrollReveal>
        </section>
    );
};

/**
 * The homepage's `revamp_2026` design of this section, or its previous one
 * while the flag is off.
 */
export const DiscoverProjectsSection = (
    props: ComponentProps<typeof DiscoverProjectsSectionRevamp> &
        ComponentProps<typeof DiscoverProjectsSectionLegacy>,
) =>
    useHomePageRevamp() ? (
        <DiscoverProjectsSectionRevamp {...props} />
    ) : (
        <DiscoverProjectsSectionLegacy {...props} />
    );
